import React from 'react';

import {assert} from 'Asserts';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {assertIsAccessible} from 'graphql-client/InaccessibleInfo';
import {GetPipelineConfigSpecType} from 'pipelines/StepShapeUtils';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {isJSONArray, JSONArray, JSONObject, JSONValue} from 'util/Types';

import jsonSpecToInitialValue from '../initial-values/jsonSpecToInitialValue';
import {maybeGetValueFromConfig, setValueInConfig} from '../step-config-settings/StepConfigSettingsUtils';
import {useStepConfigEditorContext, getDataFrameInfoOrEmptyInfo} from '../StepConfigEditorContext';
import {
  ConfigShapePath,
  ConfigDataFrameInfo,
  FieldUISchemaWithStandardComponent,
  ShapeUISchema,
  MappingArrayFieldSettings,
  FIELD_DEFAULTS_GENERATOR_KEY,
  isShapeUISchema,
} from '../StepConfigEditorTypes';
import {
  GetDataFrameUniqueValues,
  GetDataFrameUniqueValuesVariables,
} from './__generated/GetDataFrameUniqueValues';
import ArrayEditor from './ArrayEditor';
import {GET_DATA_FRAME_UNIQUE_VALUES_QUERY} from './DataFrameColumnValueEditor';

/** @package */
export interface QueryingMappingArrayEditorProps {
  config: JSONObject;
  configShape: GetPipelineConfigSpecType<'array'>;
  configShapePath: ConfigShapePath;
  onChangeConfig: (config: JSONObject, path: ConfigShapePath, newValue: JSONValue) => void;
  elementUISchema?: FieldUISchemaWithStandardComponent | ShapeUISchema;
  disabled?: boolean;
  mappingSettings: MappingArrayFieldSettings;
}

/**
 * Handles the mapping array editor when no source column is selected.
 *
 * Renders a single-element array with a sentinel value that gets transformed
 * between display (user-friendly name) and storage (sentinel constant).
 * This allows users to configure default values even when no mapping source exists.
 *
 * @example
 * When noSeriesDisplayName="Base color", user sees and edits:
 *   [{series: "Base color", color: "#3B82F6"}]
 * But stores as:
 *   [{series: "__BENCHLING_NO_SERIES_DEFAULT__", color: "#3B82F6"}]
 */
function UnmappedMappingArrayEditor(
  props: QueryingMappingArrayEditorProps & {sentinel: string}
): JSX.Element {
  const {config, configShape, configShapePath, mappingSettings, elementUISchema, sentinel} = props;
  const {dataFramesInfos} = useStepConfigEditorContext();

  const existingArray = (maybeGetValueFromConfig(config, configShapePath) || []) as Array<JSONObject>;
  const displayName = mappingSettings.noMappingDisplayName ?? 'Default';

  // Build a single-element array for the unmapped case using sentinel value
  const builtArray = buildArrayFromUniqueValues(
    [sentinel],
    existingArray,
    config,
    configShape,
    mappingSettings,
    elementUISchema,
    dataFramesInfos
  );

  // Transform sentinel to display name for UI
  const builtArrayForDisplay = transformArrayForDisplay(
    builtArray,
    mappingSettings.mappingKey,
    displayName,
    sentinel
  );

  const virtualConfig = setValueInConfig(config, configShapePath, builtArrayForDisplay);

  // Intercept changes to transform display name back to sentinel for storage
  const onChangeConfigWithSentinelTransform = (
    newConfig: JSONObject,
    path: ConfigShapePath,
    newValue: JSONValue
  ): void => {
    if (JSON.stringify(path) === JSON.stringify(configShapePath) && isJSONArray(newValue)) {
      const transformedValue = transformArrayForStorage(
        newValue,
        mappingSettings.mappingKey,
        displayName,
        sentinel
      );
      props.onChangeConfig(config, path, transformedValue);
    } else {
      props.onChangeConfig(newConfig, path, newValue);
    }
  };

  return (
    <ArrayEditor
      config={virtualConfig}
      configShape={configShape}
      configShapePath={configShapePath}
      onChangeConfig={onChangeConfigWithSentinelTransform}
      elementUISchema={elementUISchema}
      disableAddRemove={true}
      disabled={props.disabled}
    />
  );
}

/**
 * Routes between unmapped and querying mapping array cases based on source column selection.
 *
 * When a source column is selected:
 * - Queries unique values from the dataframe column
 * - Builds an array with one element per unique value
 * - Preserves user edits when source column remains the same (matches by mapping key)
 * - Resets to defaults when source column changes
 *
 * When no source column is selected:
 * - Delegates to UnmappedMappingArrayEditor if showNoSeriesEditor is enabled
 * - Shows prompt message otherwise
 */
export default function QueryingMappingArrayEditor(props: QueryingMappingArrayEditorProps): JSX.Element {
  const {config, configShape, configShapePath, mappingSettings, elementUISchema} = props;
  const {dataFramesInfos} = useStepConfigEditorContext();

  const sourceColumnName = maybeGetValueFromConfig(config, mappingSettings.mappingSourcePath) as
    | string
    | null;

  if (!sourceColumnName) {
    if (mappingSettings.showNoMappingEditor && mappingSettings.noMappingSentinel != null) {
      return <UnmappedMappingArrayEditor {...props} sentinel={mappingSettings.noMappingSentinel} />;
    } else {
      return <div>Select a column to configure mappings</div>;
    }
  }

  const firstInputDataName = Object.keys(dataFramesInfos)[0];
  const dataFrameId = firstInputDataName
    ? getDataFrameInfoOrEmptyInfo(dataFramesInfos, firstInputDataName).id
    : null;
  assert(dataFrameId != null, 'No dataFrameId available');

  return (
    <BenchlingQuery<GetDataFrameUniqueValues, GetDataFrameUniqueValuesVariables>
      query={GET_DATA_FRAME_UNIQUE_VALUES_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{
        dataFrameId,
        columnSystemName: sourceColumnName,
        limit: 1000,
      }}
    >
      {({dataFrame}) => {
        const uniqueColumnValues = dataFrame != null ? assertIsAccessible(dataFrame).uniqueValues : [];

        const existingArray = (maybeGetValueFromConfig(config, configShapePath) || []) as Array<JSONObject>;

        const builtArray = buildArrayFromUniqueValues(
          uniqueColumnValues,
          existingArray,
          config,
          configShape,
          mappingSettings,
          elementUISchema,
          dataFramesInfos
        );

        // Create a virtual config with the built array for display
        const virtualConfig = setValueInConfig(config, configShapePath, builtArray);

        return (
          <ArrayEditor
            config={virtualConfig}
            configShape={configShape}
            configShapePath={configShapePath}
            onChangeConfig={props.onChangeConfig}
            elementUISchema={elementUISchema}
            disableAddRemove={true}
            disabled={props.disabled}
          />
        );
      }}
    </BenchlingQuery>
  );
}

function transformArrayForDisplay(
  array: Array<JSONObject>,
  mappingKey: string,
  displayValue: string,
  sentinel: string
): Array<JSONObject> {
  return array.map((element) => ({
    ...element,
    [mappingKey]: element[mappingKey] === sentinel ? displayValue : element[mappingKey],
  }));
}

function transformArrayForStorage(
  array: JSONArray,
  mappingKey: string,
  displayValue: string,
  sentinel: string
): Array<JSONObject> {
  return array.map((element: JSONObject) => ({
    ...element,
    [mappingKey]: element[mappingKey] === displayValue ? sentinel : element[mappingKey],
  }));
}

/**
 * Builds an array by merging unique column values with an existing array, preserving user edits.
 *
 * Returns an array with one element per unique value. Elements are matched by the mapping key:
 * - Matching keys → preserved (keeps user edits)
 * - New keys → created with defaults
 * - Missing keys → removed
 *
 * @example Chart series colors
 * Input:
 *   uniqueColumnValues: ["Control", "Drug A", "Drug B"]
 *   existingArray: [{seriesValue: "Control", color: "#FF0000"}]
 *   mappingKey: "seriesValue"
 *
 * Output:
 *   [{seriesValue: "Control", color: "#FF0000"},    // Preserved
 *    {seriesValue: "Drug A", color: "#3B82F6"},     // New with default
 *    {seriesValue: "Drug B", color: "#8B5CF6"}]     // New with default
 */
function buildArrayFromUniqueValues(
  uniqueColumnValues: Array<string>,
  existingArray: Array<JSONObject>,
  config: JSONObject,
  configShape: GetPipelineConfigSpecType<'array'>,
  mappingSettings: MappingArrayFieldSettings,
  elementUISchema: FieldUISchemaWithStandardComponent | ShapeUISchema | undefined,
  dataFramesInfos: Record<string, Array<ConfigDataFrameInfo>>
): Array<JSONObject> {
  const elementShape = configShape.elementSpec;
  assert(elementShape.kind === 'shape', 'MAPPING_ARRAY only supports shape elements');

  // Create a map of existing elements by their mapping key
  const existingElementsMap = new Map<string, JSONObject>();
  existingArray.forEach((element: JSONObject) => {
    const key = String(element[mappingSettings.mappingKey]);
    existingElementsMap.set(key, element);
  });

  return uniqueColumnValues.map((uniqueColumnValue, idx): JSONObject => {
    const mappingKeyValue = String(uniqueColumnValue);

    const existingElement = existingElementsMap.get(mappingKeyValue);
    if (existingElement) {
      return existingElement;
    }

    const element: JSONObject = {
      [mappingSettings.mappingKey]: mappingKeyValue,
    };

    // Set default values for the element using default generator if available
    const hasGenerator =
      elementUISchema && isShapeUISchema(elementUISchema) && elementUISchema[FIELD_DEFAULTS_GENERATOR_KEY];
    if (hasGenerator) {
      const customFields = elementUISchema[FIELD_DEFAULTS_GENERATOR_KEY]!(config, dataFramesInfos, idx);
      Object.assign(element, customFields);
    } else {
      Object.entries(elementShape.fields).forEach(([fieldName, fieldSpec]) => {
        if (fieldName !== mappingSettings.mappingKey) {
          element[fieldName] = jsonSpecToInitialValue(fieldSpec, elementUISchema);
        }
      });
    }

    return element;
  });
}
